"use client";

import { RegisteredComponent } from "@builder.io/sdk-react";
import dynamic from "next/dynamic";

import { IMAGE_FILE_TYPES } from "@/libs/builder/consts";

export const checkoutInputs = [
  {
    name: "loyalty",
    type: "object",
    required: true,
    defaultValue: {
      learnMoreUrl: "/",
      orderConfirmationLoyaltyUrl: "/",
      redemptionTabSymbol: "$",
      showMgm: false,
      showReminderDialog: false,
    },
    subFields: [
      {
        name: "learnMoreUrl",
        type: "url",
        required: true,
      },
      {
        name: "orderConfirmationLoyaltyUrl",
        type: "url",
        required: true,
      },
      {
        name: "redemptionTabSymbol",
        type: "string",
        required: true,
      },
      {
        name: "showMgm",
        friendlyName: "Show or hide MGM component",
        type: "boolean",
        required: true,
      },
      {
        name: "showReminderDialog",
        friendlyName: "Show or hide reminder dialog",
        type: "boolean",
        required: true,
      },
      {
        name: "reminderDialogTitle",
        type: "string",
        required: true,
        defaultValue: "Title",
      },
      {
        name: "reminderDialogSubtitle",
        type: "string",
        required: true,
        defaultValue: "Subtitle",
      },
      {
        name: "reminderDialogText",
        type: "string",
        required: true,
        defaultValue: "Text",
      },
      {
        name: "reminderDialogSecondaryButtonText",
        type: "string",
        required: true,
        defaultValue: "Secondary Button Text",
      },
      {
        name: "reminderDialogSecondaryButtonLink",
        type: "url",
        required: true,
        defaultValue: "Secondary Button Link",
      },
    ],
  },
  {
    name: "deliveryOptions",
    type: "list",
    required: true,
    defaultValue: [
      {
        label: "Nova Poshta",
        widgetTagName: "nova-poshta-widget",
        widgetUrl:
          "https://custom-delivery-widget-des-aem-playground-e38bb9ef65cf215a050ef.gitlab-pages.jtides.com/nova-poshta-iframe.js",
        widgetVariant: "modal",
      },
    ],
    subFields: [
      {
        name: "defaultDelivery",
        type: "boolean",
        required: true,
      },
      {
        name: "enableStoreFilteringByAttributes",
        type: "string",
        enum: ["", "flavor_label", "color_label", "both"],
      },
      {
        name: "label",
        type: "string",
        required: true,
      },
      {
        name: "methodCode",
        type: "string",
        required: true,
      },
      {
        name: "widgetTagName",
        type: "string",
        required: false,
      },
      {
        name: "widgetUrl",
        type: "string",
        required: false,
      },
      {
        name: "widgetVariant",
        type: "string",
        enum: ["embedded", "modal"],
        required: false,
      },
      {
        name: "lat",
        type: "string",
      },
      {
        name: "lon",
        type: "string",
      },
      {
        name: "storeLocatorShopDetailsPage",
        type: "url",
        required: false,
      },
      {
        name: "flotiqContentType",
        type: "string",
        required: false,
      },
      {
        name: "flotiqApiKey",
        type: "string",
        required: false,
      },
      {
        name: "description",
        type: "richText",
      },
    ],
  },
  {
    name: "payOnDelivery",
    type: "object",
    required: true,
    defaultValue: {
      paymentMethod:
        "You have chosen payment on delivery, please make sure to follow up with delivery and have the exact amount when you receive the product.",
    },
    subFields: [
      {
        name: "paymentMethod",
        type: "richText",
        required: true,
      },
    ],
  },
  {
    name: "zeroValueOrder",
    type: "object",
    required: true,
    defaultValue: {
      userNotification:
        "Your order is free of charge. You will receive an email confirmation shortly.",
    },
    subFields: [
      {
        name: "userNotification",
        type: "richText",
        required: true,
      },
    ],
  },
  {
    name: "taxInfo",
    type: "object",
    defaultValue: {
      message: "The price includes 23% VAT.",
      visible: true,
    },
    subFields: [
      {
        name: "message",
        type: "richText",
        required: true,
      },
      {
        name: "visible",
        type: "boolean",
        required: true,
      },
    ],
  },
  {
    name: "failedPaymentFlow",
    type: "object",
    defaultValue: {
      title: "Payment Failed",
      message:
        "Your payment has failed. Please try again or contact us for further assistance.",
      retryPaymentButtonLabel: "Retry Payment",
    },
    subFields: [
      {
        name: "title",
        type: "string",
        required: true,
      },
      {
        name: "message",
        type: "richText",
        required: true,
      },
      {
        name: "retryPaymentButtonLabel",
        type: "string",
        required: true,
      },
    ],
  },
  {
    name: "contactConfig",
    type: "object",
    subFields: [
      {
        name: "differentRecipientDisabled",
        type: "boolean",
      },
      {
        name: "populatedPhoneInput",
        type: "boolean",
      },
    ],
  },
  {
    name: "discountDisclaimer",
    type: "object",
    required: true,
    defaultValue: {
      enabled: false,
      enabledWhenZeroValueDeposit: false,
      disclaimerMessage:
        "Promo codes and loyalty points can not be applied to tobacco products. To learn more please read out terms and conditions.",
      warningMessage: "Minimum payable amount for your current cart is {0}",
    },
    subFields: [
      {
        name: "enabled",
        type: "boolean",
        required: true,
      },
      {
        name: "enabledWhenZeroValueDeposit",
        type: "boolean",
        required: true,
      },
      {
        name: "disclaimerMessage",
        type: "richText",
        required: true,
      },
      {
        name: "warningMessage",
        type: "richText",
        required: true,
      },
    ],
  },
  {
    name: "ageVerificationPopup",
    type: "object",
    subFields: [
      {
        name: "description",
        type: "richText",
        required: true,
      },
      {
        name: "successMessage",
        type: "richText",
        required: true,
      },
      {
        name: "popupMode",
        type: "boolean",
        required: true,
      },
    ],
  },
  {
    name: "consentReminderPopup",
    type: "object",
    defaultValue: {
      consentReminderPopupEnabled: false,
      mainImage: "",
      imageAltText: "alt text",
      subtitle: "Great choice! Your order is confirmed.",
      mainQuestion:
        "Want to get the latest Offers and Product Information about EVO and Ploom?",
      description:
        "If you agree, a new tab will open to confirm your preferences while we take you to your order confirmation page.",
      yesButtonText: "Yes, keep me updated",
      noButtonText: "Not now",
      yesRedirectUrl: "https://www.google.com",
    },
    subFields: [
      {
        name: "consentReminderPopupEnabled",
        type: "boolean",
        required: true,
      },
      {
        name: "mainImage",
        required: true,
        type: "file",
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
      {
        name: "imageAltText",
        type: "string",
        required: true,
      },
      {
        name: "subtitle",
        type: "string",
        required: true,
      },
      {
        name: "mainQuestion",
        type: "string",
        required: true,
      },
      {
        name: "description",
        type: "string",
        required: true,
      },
      {
        name: "yesButtonText",
        type: "string",
        required: true,
      },
      {
        name: "noButtonText",
        type: "string",
        required: true,
      },
      {
        name: "yesRedirectUrl",
        type: "url",
        required: true,
      },
    ],
  },
] as const satisfies Inputs;

export const CheckoutComponent: RegisteredComponent = {
  name: "Checkout",
  component: dynamic(async () => (await import("./Checkout")).Checkout, {
    ssr: false,
  }),
  inputs: checkoutInputs,
};
